import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { DeckGooeyLogo } from '~/landing_page/components/DeckGooeyLogo';
import { HeroAppFrame } from '~/landing_page/components/HeroAppFrame';
import { HeroWindowDemo } from '~/landing_page/components/HeroAppFrame/HeroWindowDemo';
import { cn } from '~/lib/utils';
import styles from './HeroSection.module.css';

/**
 * One full pass of the scripted demo runs about 16s, so the title bar has to hold a file long enough
 * for its story to finish. The bare chrome's 4.2s would swap files while the sources were still
 * landing on the canvas.
 */
const DEMO_DWELL_MS = 17000;

/* Figma hard-breaks this line after "explain"; the <br> drops out below 768px so the copy reflows. */
const DEFAULT_SUBHEAD = (
  <>
    Your AI storyteller teammate that helps you explain{' '}
    <br className={styles.subheadBreak} />
    and present in the most impressive way.
  </>
);

/* "work" is the word in the frame — the rest carry the same idea into the things people actually present. */
const DEFAULT_HEADLINE_ROTATING_WORDS = [
  'work',
  'ideas',
  'research',
  'roadmap',
  'launch',
  'findings',
  'quarter',
  'side project',
] as const;

const HEADLINE_TYPE_MS = 72;
const HEADLINE_HOLD_MS = 2400;
const HEADLINE_DELETE_MS = 48;
const HEADLINE_BETWEEN_MS = 400;

const HeroRotatingWord = ({ words }: { words: readonly string[] }) => {
  const [displayed, setDisplayed] = useState(() => words[0] ?? '');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearTimer = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    if (words.length === 0) {
      setDisplayed('');
      return () => clearTimer();
    }

    setDisplayed(words[0] ?? '');

    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return () => clearTimer();
    }

    let wordIndex = 0;
    let charIndex = words[0]?.length ?? 0;
    let deleting = false;

    const schedule = (fn: () => void, ms: number) => {
      clearTimer();
      timeoutRef.current = setTimeout(fn, ms);
    };

    const step = () => {
      const word = words[wordIndex];
      if (!word) {
        wordIndex = (wordIndex + 1) % words.length;
        schedule(step, HEADLINE_BETWEEN_MS);
        return;
      }

      if (!deleting) {
        if (charIndex < word.length) {
          charIndex += 1;
          setDisplayed(word.slice(0, charIndex));
          schedule(step, HEADLINE_TYPE_MS);
        } else {
          schedule(() => {
            deleting = true;
            step();
          }, HEADLINE_HOLD_MS);
        }
      } else if (charIndex > 0) {
        charIndex -= 1;
        setDisplayed(word.slice(0, charIndex));
        schedule(step, HEADLINE_DELETE_MS);
      } else {
        deleting = false;
        wordIndex = (wordIndex + 1) % words.length;
        schedule(step, HEADLINE_BETWEEN_MS);
      }
    };

    schedule(() => {
      deleting = true;
      step();
    }, HEADLINE_HOLD_MS);

    return () => clearTimer();
  }, [words]);

  return (
    <span className={styles.headlineWordSlot}>
      <span className={styles.headlineRotatingText}>{displayed}</span>
      <span className={styles.headlineCaret} />
    </span>
  );
};

export interface HeroSectionProps {
  className?: string;
  /** Replaces the static product mock — pass the live demo here when it's ready. */
  demoSlot?: ReactNode;
  headlinePrefix?: string;
  headlineRotatingWords?: readonly string[];
  subhead?: ReactNode;
  webCtaLabel?: string;
  webCtaHref?: string;
  macCtaLabel?: string;
  macCtaHref?: string;
}

export function HeroSection({
  className,
  demoSlot,
  headlinePrefix = 'presents your',
  headlineRotatingWords = DEFAULT_HEADLINE_ROTATING_WORDS,
  subhead = DEFAULT_SUBHEAD,
  webCtaLabel = 'Continue in web',
  webCtaHref = '/sign-in',
  macCtaLabel = 'Download Mac app',
  macCtaHref = '/sign-in',
}: HeroSectionProps) {
  // The typewriter would otherwise be announced a character at a time, so the heading carries one
  // static label and everything inside it is hidden from the accessibility tree.
  const headlineLabel = `Deck ${headlinePrefix} ${headlineRotatingWords[0] ?? ''}`.trim();

  return (
    <section className={cn(styles.hero, className)} data-name="HeroSection">
      <div className={styles.heroContainer}>
        <div className={styles.headerContent}>
          <h1 className={styles.headline} aria-label={headlineLabel}>
            <span className={styles.headlineInner} aria-hidden="true">
              <DeckGooeyLogo className={styles.headlineLogo} blurScale={2.3} />
              <span className={styles.headlineText}>
                {headlinePrefix} <HeroRotatingWord words={headlineRotatingWords} />
              </span>
            </span>
          </h1>

          <p className={styles.subhead}>{subhead}</p>

          <div className={styles.actions}>
            <a className={styles.webCta} href={webCtaHref}>
              <img
                className={styles.ctaIcon}
                src="/landing/hero/icon-web.svg"
                alt=""
                width={18}
                height={18}
              />
              {webCtaLabel}
            </a>
            <a className={styles.macCta} href={macCtaHref}>
              <img
                className={styles.ctaIcon}
                src="/landing/hero/icon-mac.svg"
                alt=""
                width={18}
                height={18}
              />
              {macCtaLabel}
            </a>
          </div>
        </div>

        <div className={styles.visualFrame} data-name="HeroVisual">
          {demoSlot ?? (
            <HeroAppFrame
              dwellMs={DEMO_DWELL_MS}
              renderBody={(index) => <HeroWindowDemo index={index} />}
            />
          )}
        </div>
      </div>
    </section>
  );
}

HeroSection.displayName = 'HeroSection';
